import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, firebaseLogin, firebaseRegister, firebaseLogout, onAuthChange, getFirebaseToken } from '../lib/firebase';
import api from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseUser, setFirebaseUser] = useState(null);

  // Sync user data from backend
  const syncUserWithBackend = useCallback(async (fbUser) => {
    if (!fbUser) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const token = await fbUser.getIdToken();
      localStorage.setItem('token', token);
      
      // Try to get user from backend
      const response = await api.get('/auth/me');
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      // User not in backend — sign out of Firebase to prevent reload loop
      console.log('User not found in backend, signing out Firebase session');
      await firebaseLogout();
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (fbUser) => {
      setFirebaseUser(fbUser);
      await syncUserWithBackend(fbUser);
    });

    return () => unsubscribe();
  }, [syncUserWithBackend]);

  // Refresh token periodically
  useEffect(() => {
    const interval = setInterval(async () => {
      if (firebaseUser) {
        const token = await getFirebaseToken();
        if (token) {
          localStorage.setItem('token', token);
        }
      }
    }, 10 * 60 * 1000); // Refresh every 10 minutes

    return () => clearInterval(interval);
  }, [firebaseUser]);

  const login = async (email, password) => {
    const fbUser = await firebaseLogin(email, password);
    const token = await fbUser.getIdToken();
    localStorage.setItem('token', token);
    
    // Get user data from backend
    const response = await api.get('/auth/me');
    setUser(response.data);
    localStorage.setItem('user', JSON.stringify(response.data));
    
    return response.data;
  };

  const register = async (data) => {
    // Register with Firebase
    const fbUser = await firebaseRegister(data.email, data.password, data.name);
    const token = await fbUser.getIdToken();
    localStorage.setItem('token', token);
    
    // Register in backend (sync user data)
    const response = await api.post('/auth/firebase-register', {
      email: data.email,
      name: data.name,
      role: data.role,
      phone: data.phone,
      firebase_uid: fbUser.uid,
      organisation_name: data.organisation_name || undefined,
    });
    
    setUser(response.data.user);
    localStorage.setItem('user', JSON.stringify(response.data.user));
    
    return response.data.user;
  };

  const logout = async () => {
    await firebaseLogout();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setFirebaseUser(null);
  };

  const value = {
    user,
    firebaseUser,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isLandlord: user?.role === 'LANDLORD' || user?.role === 'SUPER_ADMIN',
    isTenant: user?.role === 'TENANT',
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
